/*
 * SARBatchUploader — modal para lanzar un lote de SAR desde un CSV.
 *
 * CSV esperado:
 *   address,operator_code,height_m,notes
 *
 * El backend geocodifica cada fila y lanza un job SAR con concurrencia limitada
 * (máx 3 simultáneos). Cada fila termina como una prospección `draft`.
 */
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '../../api/client';
import { Upload, X, FileText, Loader2, Info } from 'lucide-react';
import OperatorSelect from './OperatorSelect';

const SAMPLE = `address,operator_code,height_m,notes
Calle Mayor 1, Madrid,SPA-MA00001,25,Zona comercial
Plaza del Ayuntamiento, Valencia,,18,
Rambla Catalunya 88, Barcelona,SPA-BX00042,22,Edificio residencial`;

const SARBatchUploader = ({ onClose, onStarted }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [operator, setOperator] = useState(null); // {id?, name?, color?, logo_b64?}

  const parseFile = async (f) => {
    setError('');
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      setError('El archivo debe ser un .csv');
      return;
    }
    const text = await f.text();
    // Preview local (backend valida definitivamente)
    const lines = text.split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) { setError('El CSV está vacío o no tiene filas'); return; }
    const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
    if (!headers.includes('address')) { setError('Falta la columna "address"'); return; }
    const data = lines.slice(1, 21).map((line) => {  // preview max 20
      const cols = line.split(',');
      return Object.fromEntries(headers.map((h, i) => [h, (cols[i] || '').trim()]));
    });
    setFile(f);
    setRows(data);
  };

  const submit = async () => {
    if (!file) return;
    if (!operator?.id && !operator?.name) {
      toast.error('Elige un operador antes de lanzar el lote');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (operator) {
        if (operator.id) fd.append('operator_id', operator.id);
        if (operator.name) fd.append('operator_name', operator.name);
        if (operator.color) fd.append('operator_color', operator.color);
        if (operator.logo_b64) fd.append('operator_logo_b64', operator.logo_b64);
      }
      const { data } = await api.post('/research/sar/batch', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Lote iniciado · ${data.total} direcciones`);
      if (onStarted) onStarted(data.batch_id);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error subiendo el CSV');
    } finally { setUploading(false); }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'sar_lote_ejemplo.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-[10001] bg-black/80 backdrop-blur flex items-center justify-center p-4"
      onClick={onClose} data-testid="sar-batch-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-[#0a0a0a] border border-white/15 rounded-3xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="p-5 border-b border-white/10 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">SAR EN LOTE</div>
            <h2 className="text-xl font-black text-white mt-1">Genera varios SAR desde un CSV</h2>
            <p className="text-white/50 text-xs mt-1">Sube un CSV con hasta 20 direcciones. El sistema geocodifica cada una y crea una prospección `draft` con SAR incrustado (concurrencia 3).</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white p-1" data-testid="close-batch-modal"><X size={22}/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Info + sample */}
          <div className="bg-[#A3FF12]/5 border border-[#A3FF12]/20 rounded-lg p-3 flex items-start gap-2">
            <Info className="text-[#A3FF12] flex-shrink-0 mt-0.5" size={14}/>
            <div className="text-white/70 text-xs">
              Columnas esperadas: <code className="text-[#A3FF12]">address</code> (obligatoria),{' '}
              <code className="text-[#A3FF12]">operator_code</code>, <code className="text-[#A3FF12]">height_m</code>,{' '}
              <code className="text-[#A3FF12]">notes</code>.
              <button onClick={downloadSample}
                data-testid="download-sample-csv"
                className="ml-2 text-[#A3FF12] font-bold hover:underline">Descargar plantilla</button>
            </div>
          </div>

          {/* File picker */}
          <div>
            <input ref={inputRef} type="file" accept=".csv" hidden
              data-testid="batch-file-input"
              onChange={(e) => parseFile(e.target.files?.[0])}/>
            <button onClick={() => inputRef.current?.click()}
              data-testid="batch-pick-file"
              className="w-full bg-black border-2 border-dashed border-white/15 hover:border-[#A3FF12]/50 rounded-xl p-6 text-center transition-colors">
              <Upload className="text-[#A3FF12] mx-auto mb-2" size={20}/>
              <div className="text-white text-sm font-bold">{file ? file.name : 'Selecciona un CSV'}</div>
              <div className="text-white/40 text-[11px] mt-1">Máx 20 filas · UTF-8</div>
            </button>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-300 text-xs">
              {error}
            </div>
          )}

          {/* Preview */}
          {rows.length > 0 && (
            <div className="bg-black/40 border border-white/10 rounded-xl overflow-hidden" data-testid="batch-preview">
              <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between">
                <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">
                  VISTA PREVIA · {rows.length} FILA{rows.length === 1 ? '' : 'S'}
                </div>
                {rows.length >= 20 && <span className="text-[10px] text-orange-300">Máx 20, resto ignorado</span>}
              </div>
              <div className="max-h-52 overflow-y-auto divide-y divide-white/5">
                {rows.map((r, i) => (
                  <div key={i} className="px-3 py-2 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-white/40 text-[10px] font-mono w-6">{i + 1}</span>
                      <span className="text-white flex-1 truncate">{r.address}</span>
                      {r.operator_code && <span className="text-[#A3FF12] font-bold text-[10px]">{r.operator_code}</span>}
                    </div>
                    {(r.notes || r.height_m) && (
                      <div className="text-white/40 text-[10px] mt-0.5 ml-8">
                        {r.height_m && `${r.height_m} m`}
                        {r.height_m && r.notes && ' · '}
                        {r.notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {rows.length > 0 && (
            <div className="bg-black/30 border border-white/10 rounded-xl p-3" data-testid="batch-operator-picker">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-black tracking-widest text-[#A3FF12]">OPERADOR *</span>
                <span className="text-[10px] text-white/50">(obligatorio para todo el lote)</span>
              </div>
              <OperatorSelect value={operator} onChange={setOperator}/>
              <p className="text-white/40 text-[10px] mt-2">Se aplicará el mismo operador a todos los SAR del lote.</p>
            </div>
          )}

          {rows.length > 0 && (
            <button onClick={submit} disabled={uploading || (!operator?.id && !operator?.name)}
              data-testid="batch-submit"
              className="w-full bg-[#A3FF12] text-black font-black py-3 rounded-xl text-sm hover:bg-[#b8ff3a] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {uploading
                ? <><Loader2 className="animate-spin" size={14}/> Iniciando lote…</>
                : (!operator?.id && !operator?.name)
                  ? <>Elige un operador</>
                  : <><FileText size={14}/> Iniciar lote de {rows.length} SAR</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SARBatchUploader;
